/**
 * Push subscription flow. Requests notification permission (must be
 * triggered by a user gesture — we never auto-prompt on load), then
 * subscribes via pushManager and POSTs the subscription to the backend
 * (Phase 2.12 endpoint).
 *
 * iOS quirk: Web Push only delivers when the PWA is installed to the
 * home screen AND has been opened at least once. The pairing flow
 * already requires standalone mode, so by the time a tech reaches the
 * "Enable alerts" button this is satisfied.
 */
import { apiClient, HttpError } from '../api/client';
import { logEvent } from '../sync/log';

export type PushStatus =
  | 'unsupported' // no Notification / PushManager / SW
  | 'denied' // user said no (or browser blocked)
  | 'default' // not yet asked
  | 'granted-unsubscribed' // permission granted but no active subscription
  | 'subscribed' // permission granted + active subscription
  | 'server-not-configured'; // VAPID keys missing on the backend

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export async function getPushStatus(): Promise<PushStatus> {
  if (
    typeof Notification === 'undefined' ||
    typeof navigator === 'undefined' ||
    !('serviceWorker' in navigator) ||
    !('PushManager' in window)
  ) {
    return 'unsupported';
  }
  if (Notification.permission === 'denied') return 'denied';
  if (Notification.permission === 'default') return 'default';
  // Granted — check for an active subscription.
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    return sub ? 'subscribed' : 'granted-unsubscribed';
  } catch {
    return 'granted-unsubscribed';
  }
}

/**
 * Full enable flow. Returns the new status. Throws only on unexpected
 * errors; "permission denied" and "server not configured" come back as
 * statuses, not exceptions.
 */
export async function enablePush(): Promise<PushStatus> {
  if (
    typeof Notification === 'undefined' ||
    !('serviceWorker' in navigator) ||
    !('PushManager' in window)
  ) {
    return 'unsupported';
  }

  // Fetch the VAPID public key first — if push isn't configured
  // server-side, bail before prompting the user (no point asking).
  let vapidPublic: string;
  try {
    const resp = await apiClient.getJson<{ public_key: string }>(
      '/api/v1/auth/device/push/vapid-public-key',
    );
    vapidPublic = resp.public_key;
  } catch (err) {
    if (err instanceof HttpError && err.status === 503) {
      return 'server-not-configured';
    }
    throw err;
  }

  // Permission prompt — must be in the user-gesture call stack.
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') {
    await logEvent('push.permission_denied', {});
    return perm === 'denied' ? 'denied' : 'default';
  }

  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      // TS 5.7+ narrowed Uint8Array's buffer type; runtime is fine.
      applicationServerKey: urlBase64ToUint8Array(vapidPublic) as unknown as BufferSource,
    });
  }

  // Ship the subscription to the backend (Phase 2.12 endpoint).
  const json = sub.toJSON() as {
    endpoint: string;
    keys?: { p256dh?: string; auth?: string };
  };
  await apiClient.postJson('/api/v1/auth/device/push/subscribe', {
    endpoint: json.endpoint,
    keys_p256dh: json.keys?.p256dh ?? '',
    keys_auth: json.keys?.auth ?? '',
    ua_browser: navigator.userAgent.slice(0, 100),
  });
  await logEvent('push.subscribed', {});
  return 'subscribed';
}

export async function disablePush(): Promise<void> {
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) await sub.unsubscribe();
  } catch {
    /* best effort */
  }
  try {
    await apiClient.delete('/api/v1/auth/device/push/unsubscribe');
  } catch {
    /* best effort — backend cleanup on next push failure anyway */
  }
  await logEvent('push.unsubscribed', {});
}
